import { useEffect, useRef } from 'react';
import {
    PrequalsApi,
    PrequalificationStatus,
    RefreshPrequalResponseDto,
} from '@servicetitan/financing-api-client/src/prequals-api';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isFinalStatus } from '@servicetitan/financing-utils-shared';

const DEFAULT_POLL_INTERVAL = 5000; // 5 seconds
const DEFAULT_MAX_POLL_TIME = 120000; // 2 minutes

export interface UsePrequalStatusPollingOptions {
    /** Prequalification ID to poll for status updates */
    prequalId: string | null;
    /** Custom poll interval in milliseconds (default: 5000) */
    pollInterval?: number;
    /** Custom max poll time in milliseconds (default: 120000) */
    maxPollTime?: number;
    /** Custom timeout callback */
    onTimeout?: (prequalId: string) => void;
    /** Custom status change callback - called when prequalification status changes */
    onStatusChange?: (
        status: PrequalificationStatus,
        prequalData: RefreshPrequalResponseDto,
        prequalId: string,
    ) => void;
    /** Whether polling is enabled (default: true when prequalId exists) */
    enabled?: boolean;
}

export interface UsePrequalStatusPollingReturn {
    /** Current prequalification data from polling */

    prequalData: RefreshPrequalResponseDto | undefined;
    /** Whether polling is currently active */
    isPolling: boolean;
    /** Stop polling manually */
    stopPolling: () => void;
    /** Start polling manually */
    startPolling: () => void;
    /** Reset polling state */
    reset: () => void;
}

export const usePrequalStatusPolling = (
    options: UsePrequalStatusPollingOptions,
): UsePrequalStatusPollingReturn => {
    const {
        prequalId,
        pollInterval = DEFAULT_POLL_INTERVAL,
        maxPollTime = DEFAULT_MAX_POLL_TIME,
        onTimeout,
        onStatusChange,
        enabled = true,
    } = options;

    const [prequalsApi] = useDependencies(PrequalsApi);
    const queryClient = useQueryClient();
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const previousStatusRef = useRef<PrequalificationStatus>();

    // Set up timeout when polling starts
    useEffect(() => {
        if (prequalId && enabled) {
            timeoutRef.current = setTimeout(() => {
                // Cancel the polling query
                queryClient.cancelQueries({ queryKey: ['prequalStatus', prequalId] });

                if (onTimeout) {
                    onTimeout(prequalId);
                }
                // Note: Decision data and workflow stage management should be handled in the component
            }, maxPollTime);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [prequalId, enabled, queryClient, onTimeout, maxPollTime]);

    // Query for polling prequalification status
    const { data: prequalData, refetch } = useQuery<RefreshPrequalResponseDto>({
        queryKey: ['prequalStatus', prequalId],
        queryFn: async () => {
            // Refresh prequalification status (queries provider for updated status and returns data)
            const response = await prequalsApi.prequalificationsRefreshPrequalification(prequalId!);
            return response.data;
        },
        enabled: !!prequalId && enabled,
        refetchInterval: query => {
            // Continue polling if we don't have data yet
            if (!query.state.data?.status) {
                return pollInterval;
            }

            // Stop polling for final statuses (Approved, Declined, Prequalified, Error, Cancelled)
            if (isFinalStatus(query.state.data.status)) {
                return false;
            }

            // Continue polling for non-final statuses
            return pollInterval;
        },
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Effect to handle prequalification status changes
    useEffect(() => {
        if (prequalData?.status && prequalId) {
            // Clear timeout if we get a final status
            if (timeoutRef.current && isFinalStatus(prequalData.status)) {
                clearTimeout(timeoutRef.current);
            }

            // Call the callback if status has changed
            if (onStatusChange && previousStatusRef.current !== prequalData.status) {
                onStatusChange(prequalData.status, prequalData, prequalId);
                previousStatusRef.current = prequalData.status;
            }
        }
    }, [prequalData, prequalId, onStatusChange]);

    const stopPolling = () => {
        queryClient.cancelQueries({ queryKey: ['prequalStatus', prequalId] });
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const startPolling = () => {
        if (prequalId) {
            refetch();
        }
    };

    const reset = () => {
        stopPolling();
        previousStatusRef.current = undefined;
    };

    return {
        prequalData,
        isPolling: !!prequalId && enabled,
        stopPolling,
        startPolling,
        reset,
    };
};
